import { create } from "zustand";
import { immer } from "zustand/middleware/immer";

// Configuration constants
const QUEUE_PROCESS_DEBOUNCE_MS = 50; // Debounce time for batch processing

interface QueuedTestData {
    testId: string;
    variationId: string;
}

interface BuilderTestState {
    // Core state
    builderTestIds: string[];
    builderVariationIds: string[];

    // Queue system
    testDataQueue: QueuedTestData[];
    processingQueue: boolean;
    lastQueueProcessTime: number | null;
    queueProcessTimeout: NodeJS.Timeout | null;

    // Actions
    addTestData: (testId: string, variationId: string) => void;
    addTestDataBatch: (items: QueuedTestData[]) => void;
    processQueue: () => void;
    cancelPendingProcess: () => void;
    resetTestData: () => void;
}

/**
 * Builder Test Store - Queuing and Tracking System
 *
 * This store manages Builder.io test and variation IDs with an optimized queue system:
 *
 * How it works:
 * 1. When components detect Builder tests, they call addTestData() with test/variation IDs
 * 2. Instead of updating state immediately, IDs are added to a queue (testDataQueue)
 * 3. The queue is processed efficiently to batch multiple updates together
 * 4. Duplicate entries are automatically filtered at both queue and state levels
 *
 * Key features:
 * - Queue system prevents excessive re-renders when multiple slots load simultaneously
 * - Batch processing allows multiple test IDs to be handled in a single state update
 * - Processing status tracking prevents queue processing conflicts
 * - Debouncing of queue processing prevents rapid state updates
 * - Only updates state when actual changes exist (optimization)
 *
 * This approach significantly improves performance when multiple Builder slots
 * load on the same page and need to track test/variation IDs for analytics.
 */

// Named `use*` on purpose: this IS the React hook (zustand's bound useStore). Calling it through a
// non-`use` name lets the React Compiler treat it as a plain call and memoize/skip it on re-render,
// which shifts the caller's hook order and crashes the tree (see useOtherValuesStore.ts).
export const useBuilderTestStore = create<BuilderTestState>()(
    immer((set, get) => ({
        builderTestIds: [],
        builderVariationIds: [],
        testDataQueue: [],
        processingQueue: false,
        lastQueueProcessTime: null,
        queueProcessTimeout: null,

        addTestData: (testId: string, variationId: string) => {
            if (!testId || !variationId) return;

            // Add to queue instead of directly to state
            set(state => {
                // Check if this pair is already in the queue
                const alreadyQueued = state.testDataQueue.some((item: QueuedTestData) => item.testId === testId && item.variationId === variationId);

                if (!alreadyQueued) {
                    state.testDataQueue.push({ testId, variationId });
                }
            });

            // Try to process the queue right away
            get().processQueue();
        },

        addTestDataBatch: (items: QueuedTestData[]) => {
            if (!items.length) return;

            // Add all items to queue, avoiding duplicates
            set(state => {
                const newItems = items.filter(
                    (newItem: QueuedTestData) =>
                        !state.testDataQueue.some((existingItem: QueuedTestData) => existingItem.testId === newItem.testId && existingItem.variationId === newItem.variationId)
                );

                state.testDataQueue.push(...newItems);
            });

            // Try to process the queue right away
            get().processQueue();
        },

        processQueue: () => {
            const state = get();

            // Don't process if already processing or queue is empty
            if (state.processingQueue || state.testDataQueue.length === 0) {
                return;
            }

            // Clear any existing timeout
            if (state.queueProcessTimeout) {
                clearTimeout(state.queueProcessTimeout);
            }

            // Set a debounced timeout to process the queue
            const timeout = setTimeout(() => {
                set(state => {
                    state.processingQueue = true;
                    state.queueProcessTimeout = null;
                });

                // Use Promise to ensure proper async execution
                Promise.resolve().then(() => {
                    set(state => {
                        // Process all queued items
                        const existingTestIds = new Set(state.builderTestIds);
                        const existingVariationIds = new Set(state.builderVariationIds);

                        let hasChanges = false;

                        // Process each queued item
                        state.testDataQueue.forEach((item: QueuedTestData) => {
                            const { testId, variationId } = item;

                            if (!existingTestIds.has(testId)) {
                                existingTestIds.add(testId);
                                hasChanges = true;
                            }

                            if (!existingVariationIds.has(variationId)) {
                                existingVariationIds.add(variationId);
                                hasChanges = true;
                            }
                        });

                        // Only update state if there were actual changes
                        if (hasChanges) {
                            state.builderTestIds = Array.from(existingTestIds);
                            state.builderVariationIds = Array.from(existingVariationIds);
                        }

                        // Clear the queue and update processing status
                        state.testDataQueue = [];
                        state.processingQueue = false;
                        state.lastQueueProcessTime = Date.now();
                    });
                });
            }, QUEUE_PROCESS_DEBOUNCE_MS);

            // Store the timeout reference
            set(state => {
                state.queueProcessTimeout = timeout;
            });
        },

        cancelPendingProcess: () => {
            const state = get();

            // Clear any existing timeout
            if (state.queueProcessTimeout) {
                clearTimeout(state.queueProcessTimeout);

                set(state => {
                    state.queueProcessTimeout = null;
                    state.processingQueue = false;
                });
            }
        },

        resetTestData: () =>
            set(state => {
                // Clear any pending timeout
                if (state.queueProcessTimeout) {
                    clearTimeout(state.queueProcessTimeout);
                }

                state.builderTestIds = [];
                state.builderVariationIds = [];
                state.testDataQueue = [];
                state.processingQueue = false;
                state.queueProcessTimeout = null;
            })
    }))
);

export const useBuilderTestIds = () => useBuilderTestStore(state => state.builderTestIds);
export const useBuilderVariationIds = () => useBuilderTestStore(state => state.builderVariationIds);
export const useBuilderTestActions = () =>
    useBuilderTestStore(state => ({
        addTestData: state.addTestData,
        addTestDataBatch: state.addTestDataBatch,
        cancelPendingProcess: state.cancelPendingProcess,
        resetTestData: state.resetTestData
    }));
